/**
 * Phase 24 / Task 24.9 (DEC-100) — catalog operator-change hooks (web-dispatch).
 *
 * Hauler side (DISPATCH_MANAGER+ / DISPATCHER+ count):
 *   useReviewCount  — GET /v1/catalog-change-reviews/count  (dashboard banner)
 *   useReviews      — GET /v1/catalog-change-reviews        (review page)
 *   useReviewAction — PATCH …/:id/{accept|dismiss|defer}
 *
 * Paelode admin side (PLATFORM_ADMIN):
 *   usePlatformChangeQueue  — GET /v1/platform/catalog/change-queue (+ filters)
 *   usePlatformChangeDetail — GET …/:id (+ affected hauler tenants)
 *   usePlatformChangeAction — PATCH …/:id/{approve|reject|defer}
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  ApiError,
  apiErrorFromResponse,
  apiErrorReasonValue,
  authedFetch,
  useApiOpts,
  useApiQuery,
  type WebApiOpts,
} from '@lodex-pro/web-shared';
import type { CatalogChangeDisplay } from '@lodex-pro/shared';

// ── Hauler review types ──────────────────────────────────────────────────────
export interface HaulerReview {
  readonly id: string;
  readonly reviewStatus: string;
  readonly reviewedAt: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly changeType: string;
  readonly detectedAt: string;
  readonly paelodeApprovedAt: string | null;
  readonly wellName: string | null;
  readonly apiNumber: string | null;
  readonly oldOperatorName: string | null;
  readonly newOperatorName: string | null;
  readonly affectedTankBatteries: readonly string[];
}

export const REVIEW_COUNT_KEY = ['catalog-change-reviews', 'count'] as const;
export const REVIEWS_KEY = ['catalog-change-reviews', 'list'] as const;

export function useReviewCount(
  enabled = true,
): ReturnType<typeof useApiQuery<{ pending: number }>> {
  const opts = useApiOpts();
  return useApiQuery<{ pending: number }>(opts, '/v1/catalog-change-reviews/count', {
    queryKey: REVIEW_COUNT_KEY,
    enabled,
    staleTime: 30_000,
  });
}

export function useReviews(): ReturnType<typeof useApiQuery<{ reviews: HaulerReview[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ reviews: HaulerReview[] }>(opts, '/v1/catalog-change-reviews', {
    queryKey: REVIEWS_KEY,
    staleTime: 10_000,
  });
}

async function patchAction(opts: WebApiOpts, path: string, notes?: string): Promise<void> {
  const res = await authedFetch(opts, path, {
    method: 'PATCH',
    body: JSON.stringify(notes !== undefined && notes !== '' ? { notes } : {}),
  });
  if (!res.ok) {
    // The error code and fields, so a refusal can be shown in words (0205: accepting a change
    // that would give two customers the same ticket code names the other customer).
    throw await apiErrorFromResponse(res, path);
  }
}

/**
 * What a refused review action means, in words. Accepting re-links the wells to the new
 * operator's customer row, whose ticket code is the new operator's Paelode code; 0205 refuses it
 * when another of the hauler's customers already prints that code, and names that customer.
 */
export function reviewActionError(err: unknown): string {
  if (err instanceof ApiError && err.code === 'TICKET_PREFIX_TAKEN') {
    const other = apiErrorReasonValue(err, 'ticket_prefix_taken_by');
    return other === null
      ? 'Another of your customers already prints this operator’s ticket code. Change that customer’s code in Sites & Operators, then accept again.'
      : `${other} already prints this operator’s ticket code. Change ${other}’s code in Sites & Operators, then accept again.`;
  }
  return 'Could not update — try again';
}

export type HaulerAction = 'accept' | 'dismiss' | 'defer';

export function useReviewAction(): UseMutationResult<
  void,
  Error,
  { id: string; action: HaulerAction; notes?: string }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, notes }) =>
      patchAction(opts, `/v1/catalog-change-reviews/${id}/${action}`, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REVIEWS_KEY });
      void qc.invalidateQueries({ queryKey: REVIEW_COUNT_KEY });
    },
  });
}

// ── Paelode admin types ──────────────────────────────────────────────────────
export interface PlatformChange {
  readonly id: string;
  readonly stateAgency: string;
  readonly changeType: string;
  readonly wellId: string | null;
  readonly oldValues: Record<string, unknown>;
  readonly newValues: Record<string, unknown>;
  readonly detectedAt: string;
  readonly reviewStatus: string;
  readonly reviewedAt: string | null;
  readonly reviewNotes: string | null;
  readonly paelodeApprovedAt: string | null;
  readonly notifyHaulers: boolean;
  readonly wellName: string | null;
  readonly apiNumber: string | null;
  readonly operatorName: string | null;
  readonly affectedHaulerCount: number;
  /** The change in plain words, built by the API (ids resolved to names). */
  readonly display: CatalogChangeDisplay;
}

export interface AffectedHauler {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly batteryCount: number;
  readonly batteryNames: string | null;
  /** Active pickup-catalog rows for the well (0 or 1). */
  readonly pickupCount: number;
  /** Active disposal authorizations for the well (0 or 1). */
  readonly disposalCount: number;
  readonly reviewStatus: string | null;
}

export type PlatformReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'DEFERRED';

export const PLATFORM_QUEUE_KEY = ['platform-change-queue'] as const;

export function usePlatformChangeQueue(
  reviewStatus?: PlatformReviewStatus,
): ReturnType<typeof useApiQuery<{ changes: PlatformChange[] }>> {
  const opts = useApiOpts();
  const qs = reviewStatus !== undefined ? `?reviewStatus=${reviewStatus}` : '';
  return useApiQuery<{ changes: PlatformChange[] }>(
    opts,
    `/v1/platform/catalog/change-queue${qs}`,
    { queryKey: [...PLATFORM_QUEUE_KEY, reviewStatus ?? 'ALL'], staleTime: 10_000 },
  );
}

export function usePlatformChangeDetail(
  id: string | null,
): ReturnType<typeof useApiQuery<{ change: PlatformChange; affectedHaulers: AffectedHauler[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ change: PlatformChange; affectedHaulers: AffectedHauler[] }>(
    opts,
    `/v1/platform/catalog/change-queue/${id ?? ''}`,
    { queryKey: ['platform-change-detail', id ?? ''], enabled: id !== null, staleTime: 5_000 },
  );
}

export type AdminAction = 'approve' | 'reject' | 'defer';

export function usePlatformChangeAction(): UseMutationResult<
  void,
  Error,
  { id: string; action: AdminAction; notes?: string }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, notes }) =>
      patchAction(opts, `/v1/platform/catalog/change-queue/${id}/${action}`, notes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: ['platform-change-detail'] });
    },
  });
}
